import React, { useCallback, useEffect, useRef, useState } from 'react';
import { resolveApiUrl } from '../../apiBase';
import usePolling from '../../utils/usePolling';
import MaintenancePage from './MaintenancePage';

// 60 s i synlig flik, paus i dold flik med kontroll direkt vid återkomst
// (A11, 2026-10-07). Under underhåll svarar övriga anrop ändå 503.
const POLL_INTERVAL_MS = 60000;

/**
 * Wraps the app and shows MaintenancePage instead of children when the backend
 * reports maintenance mode. Polls /api/maintenance/status every 60s. On
 * network errors we keep showing whatever was last known so users aren't
 * bumped to maintenance because of flaky wifi.
 */
export default function MaintenanceGate({ children }) {
  const [status, setStatus] = useState({ active: false, message: null, eta: null });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const check = useCallback(async () => {
    const url = `${resolveApiUrl()}/api/maintenance/status`;
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!mountedRef.current) return true;

      if (res.status === 503) {
        // Förväntat svar under underhåll — räknas som lyckad kontroll så
        // att intervallet inte glesas ut och slutet på underhållet märks i tid.
        setStatus({ active: true, message: null, eta: null });
        return true;
      }
      if (!res.ok) return false;

      const data = await res.json();
      if (!mountedRef.current) return true;
      setStatus({
        active: !!data.active,
        message: data.message || null,
        eta: data.eta || null,
      });
      return true;
    } catch {
      // Network error — leave previous state untouched; usePolling glesar ut.
      return false;
    }
  }, []);

  usePolling(check, { intervalMs: POLL_INTERVAL_MS });

  if (status.active) {
    return <MaintenancePage message={status.message} etaIso={status.eta} />;
  }
  return children;
}
